// AuthorPageClient.jsx
"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import BlogCards from "@/components/Blogs/BlogCards";
import { AUTHOR_PAGE_SIZE } from "@/helpers/GetAuthor";
import "@/app/blog/blog.css";

const PAGE_SIZE = AUTHOR_PAGE_SIZE;


export default function AuthorPageClient({
    author,
    authorId,
    initialArticles,
    totalCount,
    pageCount,
    avatarUrl,
}) {
    const [articles, setArticles] = useState(initialArticles);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(1 < pageCount);
    const loaderRef = useRef(null);

    // Use refs to always keep the latest values accessible inside the observer
    // without triggering observer teardowns and recreations.
    const stateRef = useRef({ isLoading, hasMore, currentPage, authorId });
    
    useEffect(() => {
        stateRef.current = { isLoading, hasMore, currentPage, authorId };
    }, [isLoading, hasMore, currentPage, authorId]);

    const loadMore = useCallback(async () => {
        const state = stateRef.current;
        if (state.isLoading || !state.hasMore) return;
        
        setIsLoading(true);

        try {
            const nextPage = state.currentPage + 1;
            const res = await fetch(
                `/api/author-articles?authorId=${state.authorId}&page=${nextPage}&pageSize=${PAGE_SIZE}`
            );

            if (!res.ok) throw new Error("Failed to fetch articles");

            const { articles: newArticles, pageCount: newPageCount } = await res.json();
            
            if (newArticles && newArticles.length > 0) {
                setArticles((prev) => [...prev, ...newArticles]);
                setCurrentPage(nextPage);
                setHasMore(nextPage < newPageCount);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error("Load more error:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Bulletproof Infinite Scroll Trigger
    useEffect(() => {
        const el = loaderRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { threshold: 0.1, rootMargin: "100px" } // Pre-fetches 100px before reaching bottom
        );

        observer.observe(el);
        return () => {
            if (el) observer.unobserve(el);
        };
    }, [loadMore]);


    const authorBanner = "https://cdn.msu.edu.in/msu-reavamp/banner/author_banner.png";
    return (
        <main className="bg-white w-full mx-auto relative blog-page">

            {/* ── Hero Banner ── */}
            <section className="relative w-full h-[320px] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60 z-10" />

                {authorBanner ? (
                    <img
                        src={authorBanner}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover scale-110 blur-sm"
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900" />
                )}

                <div className="relative z-20 flex flex-col items-center justify-center h-full px-4 text-center -mt-4">
                    <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
                        {author?.name}
                    </h1>

                    {author?.Author_Bio && (
                        <p className="max-w-3xl text-sm md:text-base text-white/90 leading-relaxed line-clamp-3">
                            {author.Author_Bio}
                        </p>
                    )}
                </div>
            </section>

            {/* ── Avatar overlapping hero bottom ── */}
            <div className="flex justify-center -mt-14 relative z-30 mb-4">
                <div className="w-28 h-28 rounded-full ring-4 ring-white shadow-xl overflow-hidden bg-gray-200">
                    {avatarUrl ? (
                        <img
                            src={avatarUrl}
                            alt={author?.name || "Author"}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl font-bold text-gray-400">
                            {author?.name?.[0] || "A"}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Stories count heading ── */}
            <div className="text-center pb-6">
                <h2 className="text-2xl font-semibold text-gray-800">
                    {totalCount} {totalCount === 1 ? "Story" : "Stories"} By {author?.name}
                </h2>
                <div className="mx-auto mt-2 w-24 border-b-2 border-gray-300" />
            </div>

            {/* ── Article Grid ── */}
            <div className="page-container mx-auto pb-12 px-4">
                {articles.length === 0 ? (
                    <p className="text-center text-gray-500 py-16">
                        No articles found for this author.
                    </p>
                ) : (
                    <>
                        <BlogCards blogs={articles} />

                        {/* Skeleton cards while loading next page */}
                        {isLoading && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="flex flex-col bg-white border border-gray-100 rounded-xl overflow-hidden animate-pulse"
                                    >
                                        <div className="aspect-4/3 bg-gray-200" />
                                        <div className="p-4 flex flex-col gap-3">
                                            <div className="h-3 bg-gray-200 rounded w-1/4" />
                                            <div className="h-4 bg-gray-200 rounded w-3/4" />
                                            <div className="h-3 bg-gray-100 rounded w-full" />
                                            <div className="h-3 bg-gray-100 rounded w-5/6" />
                                            <div className="h-px bg-gray-200 mt-1" />
                                            <div className="h-3 bg-gray-100 rounded w-1/2" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Invisible sentinel — Always stays rendered when hasMore is true */}
                        {hasMore && (
                            <div ref={loaderRef} className="h-10 mt-4 w-full" aria-hidden="true" />
                        )}

                        {/* End of list text */}
                        {!hasMore && articles.length >= PAGE_SIZE && (
                            <p className="text-center text-gray-400 text-sm py-8">
                                You've seen all {totalCount} articles by {author?.name}.
                            </p>
                        )}
                    </>
                )}
            </div>
        </main>
    );
}